/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from "zod";

import { AuthRule } from "../AuthRule.js";
import { DateTimeString } from "../dateTypes.js";

/**
 * The most basic 'atom' of multiplayer, this is sent between
 * the server and clients for mutating the model.
 *
 * The type `PAYLOAD` must be serializable.
 */
export interface AtomicOperation<
  OPERATION_TYPE extends string,
  PAYLOAD extends Record<string, any>,
> {
  /**
   * Discriminant that makes it easy to determine the type of this operation
   */
  type: OPERATION_TYPE;
  /**
   * The actual contents of the operation
   */
  payload: PAYLOAD;
  /**
   * This key is banned, as it should be put on the AtomicOperationDefinition instead
   * @deprecated @see AtomicOperationDefinition["conflictId"]
   */
  conflictId?: never;
  /**
   * This key is banned, as it should be put on the AtomicOperationDefinition instead
   * @deprecated @see AtomicOperationDefinition["creationId"]
   */
  creationId?: never;
  /**
   * This key is banned, as it should be put on the AtomicOperationDefinition instead
   * @deprecated @see AtomicOperationDefinition["alwaysSkipUndoBuffer"]
   */
  alwaysSkipUndoBuffer?: never;
}

export type ConstantOrPerOp<
  T,
  CREATE extends (...args: any[]) => GenericAtomicOperation,
> =
  | T
  | ((
      op: AtomicOperation<
        ReturnType<CREATE>["type"],
        ReturnType<CREATE>["payload"]
      >,
    ) => T);

export type OpForIds<
  T,
  CREATE extends (...args: any[]) => GenericAtomicOperation,
> = (
  op: AtomicOperation<
    ReturnType<CREATE>["type"],
    ReturnType<CREATE>["payload"]
  >,
) => T;

export type AtomicOperationAuthArgName<
  CREATE extends (...args: any[]) => GenericAtomicOperation,
  MODEL_ID_ARG extends string,
> = (keyof ReturnType<CREATE>["payload"] & string) | MODEL_ID_ARG;

/**
 * Holds a payload schema to the exact key set of the payload its `create` builds.
 *
 * Assignability is not enough on its own. A schema that simply omits an optional
 * property parses to a type that is still assignable to the payload -- `{}` satisfies
 * `{ runCell?: boolean }` -- so the field compiles, and is then silently stripped on the
 * wire by the atomic-operation scalar. That is the shape of the bug fixed in "F1: stop
 * stripping cell-run control flags from update-cell payloads": success, no error, the
 * server reads `undefined` and takes the default branch.
 *
 * Comparing key sets is what catches it, since `keyof` counts an optional property the
 * same as a required one. The failing branches resolve to an object type naming the
 * offending keys, which is not assignable to a `ZodType`, so the error names them.
 *
 * Two shapes are deliberately skipped, because the comparison says nothing about them:
 *
 * - An index signature. `keyof` is then `string`, which neither names the declared keys
 *   nor can be differenced against them. `keyValueUpdatePayloadSchema` builds its options
 *   from a runtime record and so lands here; those operations are held to their field set
 *   by `satisfies Record<keyof FIELDS, z.ZodTypeAny>` at the call site instead.
 * - A union on either side, where `keyof (A | B)` is only the keys common to both, so a
 *   property in one member and not the other drops out of the comparison. A missed check
 *   rather than a false positive.
 */
type HasLiteralKeys<T> = string extends keyof T
  ? false
  : number extends keyof T
    ? false
    : true;

type PayloadKeyParity<SCHEMA_OUT, PAYLOAD> =
  HasLiteralKeys<SCHEMA_OUT> extends true
    ? HasLiteralKeys<PAYLOAD> extends true
      ? [Exclude<keyof PAYLOAD, keyof SCHEMA_OUT>] extends [never]
        ? [Exclude<keyof SCHEMA_OUT, keyof PAYLOAD>] extends [never]
          ? unknown
          : {
              __schemaDeclaresKeysThePayloadDoesNot: Exclude<
                keyof SCHEMA_OUT,
                keyof PAYLOAD
              >;
            }
        : {
            __schemaIsMissingPayloadKeys: Exclude<
              keyof PAYLOAD,
              keyof SCHEMA_OUT
            >;
          }
      : unknown
    : unknown;

/**
 * Meta data about an operation type used to properly handle
 * it both on the server and the client.
 */
export type AtomicOperationDefinition<
  CREATE extends (...args: any[]) => GenericAtomicOperation,
  EXTRA_OPTIONS = unknown,
  MODEL_ID_ARG extends string = never,
  SCHEMA extends z.ZodTypeAny = z.ZodType<
    ReturnType<CREATE>["payload"],
    z.ZodTypeDef,
    unknown
  >,
> = EXTRA_OPTIONS & {
  type: ReturnType<CREATE>["type"];
  /**
   * Schema for this operation's payload as it arrives over the wire.
   *
   * This is what lets an operation be validated by inspecting its definition: the
   * family's schema map, and so the GraphQL scalar behind it, is derived from the
   * definitions rather than maintained alongside them. Typing it against
   * `PAYLOAD` means a schema that parses to something other than what `create`
   * builds is a compile error.
   *
   * `PayloadKeyParity` additionally requires the two to declare the *same keys*, not
   * merely compatible ones -- see its doc comment for why assignability alone lets a
   * field be dropped on the wire.
   */
  payloadSchema: SCHEMA &
    PayloadKeyParity<z.infer<SCHEMA>, ReturnType<CREATE>["payload"]>;
  /**
   * Auth rule for enforcing whether or not a client can view a specific operation.
   *
   * The "arguments" this auth rule checks is the type `PAYLOAD` *NOT* the type `CREATE_ARGS`.
   * This is beacuse `create()` is called client side, so those arguments are not available to
   * the server to check.
   */
  readAuth: AuthRule<AtomicOperationAuthArgName<CREATE, MODEL_ID_ARG>>;
  /**
   * Auth rule for enforcing whether or not a client can dispatch a specific operation.
   *
   * The "arguments" this auth rule checks is the type `PAYLOAD` *NOT* the type `CREATE_ARGS`.
   * This is because `create()` is called client side, so those arguments are not available to
   * the server to check.
   */
  writeAuth: AuthRule<AtomicOperationAuthArgName<CREATE, MODEL_ID_ARG>>;
  /**
   * Which keys of type `PAYLOAD` whose values are safe to log.
   *
   * Can also be a function that takes an operation and produces a record of log safe values.
   */
  logSafe:
    | readonly (keyof ReturnType<CREATE>["payload"])[]
    | ((args: {
        operation: AtomicOperation<
          ReturnType<CREATE>["type"],
          ReturnType<CREATE>["payload"]
        >;
        dangerouslyUnsafe: boolean;
      }) => Record<string, unknown>);
  /**
   * Handy dandy function for actually constructing operations of this type.
   */
  create: CREATE;
  /**
   * Function for determining the conflict id of a specific operation.
   *
   * Any operations that share a conflict id are assumed
   * to completely overwrite each other. As such, if there are two actions
   * that would undo each other, for example, DELETE and RESTORE, they should
   * share a conflict id.
   *
   * For example, if operations A and B shared a conflict id:
   * - If A is applied first, B should completely overwrite any changes from it
   * - If B is applied first, A should completely overwrite any changes from it
   *
   * If thats not the case they should be split into two separate operations.
   */
  conflictId: OpForIds<string, CREATE>;
  /**
   * Function for determining the creation id of a specific operation.
   *
   * If an operation has a creation id, it means that
   * it should not be sent to the remote until an operation
   * with the specified creation ID has been acked
   */
  creationId?: OpForIds<string, CREATE>;
  /**
   * Whether or not operations of this type are ever added to the undo buffer.
   * If `true`, operations of this type cannot be undone.
   *
   * However, we still need to implement undo/redo client side
   * for rollbacks.
   *
   * @default false
   */
  alwaysSkipUndoBuffer?: ConstantOrPerOp<boolean, CREATE>;
};

export type AnyModelAtomicOperationDefinition<
  CREATE extends (...args: any[]) => GenericAtomicOperation,
> = AtomicOperationDefinition<CREATE, unknown, any>;

export type ExtractAtomicOperationFromDefinition<OPERATION_DEF> =
  OPERATION_DEF extends AtomicOperationDefinition<infer CREATE, unknown, any>
    ? AtomicOperation<ReturnType<CREATE>["type"], ReturnType<CREATE>["payload"]>
    : never;

/**
 * Helper function that makes it easier to make sure given operation
 * definition is well typed.
 */
export const createAtomicOperationDefinition = <
  CREATE extends (...args: any[]) => GenericAtomicOperation,
  MODEL_ID_ARG extends string = never,
  // Inferred from `baseDef`, so `PayloadKeyParity` sees the schema's own key set.
  // Defaulted, and declared last, so existing explicit type arguments still apply.
  SCHEMA extends z.ZodType<
    ReturnType<CREATE>["payload"],
    z.ZodTypeDef,
    unknown
  > = z.ZodType<ReturnType<CREATE>["payload"], z.ZodTypeDef, unknown>,
>(
  baseDef: AtomicOperationDefinition<CREATE, unknown, MODEL_ID_ARG, SCHEMA>,
): AtomicOperationDefinition<CREATE, unknown, MODEL_ID_ARG> => baseDef;

/**
 * Wrapper for an atomic operation being sent to the server
 */
export interface AtomicOperationRequest<
  OPERATION extends AtomicOperation<string, any> = AtomicOperation<
    string,
    Record<string, Record<string, any>>
  >,
> {
  /**
   * Unique indentifier of a multiplayer client.
   *
   * Used for figuring out if an operation originated from
   * a given browser tab etc, NOT a real user / api client id
   */
  mpClientId: string;
  operation: OPERATION;
}

/**
 * Singleton multiplayer client id for any operations created by the server.
 */
export const SERVER_CLIENT_ID = "server";

export type GenericAtomicOperation = AtomicOperation<
  string,
  Record<string, any>
>;

/**
 * Utility type for representing any batched set of objects in AO.
 * Atomic Operations are often represented as a 2d array that shouldn't be mutated
 * Each nested array represents a set of operations that should also be processed as a batch.
 */
export type ReadonlyBatch<O> = ReadonlyArray<ReadonlyArray<Readonly<O>>>;

/**
 * Utility type for batched AO responses, received by the client.
 */
export type BatchedAOResponses<OPERATION extends GenericAtomicOperation> =
  ReadonlyBatch<AtomicOperationResponse<OPERATION>>;

/**
 * Wrapper for an atomic operation being recieved by a client
 */
export interface AtomicOperationResponse<
  OPERATION extends AtomicOperation<string, Record<string, any>> =
    GenericAtomicOperation,
> {
  mpClientId: string;
  operation: OPERATION;
  revision: number;
  createdDate: DateTimeString;
}

export type AtomicOperationDefinitionMap<
  OPERATION extends GenericAtomicOperation,
  MODEL_PAYLOAD_ID_ARG extends string,
  EXTRA_OPTIONS = unknown,
> = {
  [OP in OPERATION as OP["type"]]: AtomicOperationDefinition<
    (
      ...args: any[]
    ) => AtomicOperation<OP["type"], Omit<OP["payload"], MODEL_PAYLOAD_ID_ARG>>,
    EXTRA_OPTIONS,
    MODEL_PAYLOAD_ID_ARG
  >;
};
